import { toWSDateString } from "@wingspanhq/fe-component-library";
import { Payable } from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";
import { Activity, ActivityItem } from "../Activity";

type Props = { payable: Payable };

export const PayableActivity: React.FC<Props> = props => {
  const { payable } = props;
  const events = payable.events;
  const payerEmail = payable.payer?.email;
  const payeeEmail = payable.payee?.email;

  const activity = useMemo(() => {
    const activity: ActivityItem[] = [];

    if (events?.createdAt) {
      activity.push({
        title: "Payable was created",
        description: toWSDateString(events.createdAt),
        date: events.createdAt
      });
    }

    if (events?.openedAt) {
      activity.push({
        title: payerEmail ? `Sent to ${payerEmail}` : "Invoice opened",
        description: toWSDateString(events.openedAt),
        date: events.openedAt
      });
    }

    if (events?.acceptedAt) {
      activity.push({
        title: "Invoice accepted by contractor",
        description: toWSDateString(events.acceptedAt),
        date: events.acceptedAt
      });
    }

    if (events?.paymentInTransitAt) {
      activity.push({
        title: `Payment in transit on ${toWSDateString(
          events.paymentInTransitAt
        )}`,
        description: "Waiting on wire transfer to arrive",
        date: events.paymentInTransitAt
      });
    }

    (events?.paymentFailedAt || []).forEach(failedAt => {
      activity.push({
        title: "Payment failed",
        description: toWSDateString(failedAt),
        date: failedAt
      });
    });

    if (events?.partiallyPaidAt) {
      activity.push({
        title: "Partially paid",
        description: toWSDateString(events.partiallyPaidAt),
        date: events.partiallyPaidAt
      });
    }

    if (events?.paidAt) {
      activity.push({
        title: `Paid on ${toWSDateString(events.paidAt)}`,
        description: "Payable was paid",
        date: events.paidAt
      });
    }

    if (events?.markedPaidAt) {
      activity.push({
        title: "Marked as paid",
        description: toWSDateString(events.markedPaidAt),
        date: events.markedPaidAt
      });
    }

    if (events?.paidOffPlatformAt) {
      activity.push({
        title: "Marked as paid off-platform",
        description: toWSDateString(events.paidOffPlatformAt),
        date: events.paidOffPlatformAt
      });
    }

    if (events?.depositConfirmedAt) {
      activity.push({
        title: "Deposit confirmed",
        description: toWSDateString(events.depositConfirmedAt),
        date: events.depositConfirmedAt
      });
    }

    if (events?.memberDisputedAt) {
      activity.push({
        title: payeeEmail ? `Disputed by ${payeeEmail}` : "Invoice disputed",
        description: toWSDateString(events.memberDisputedAt),
        date: events.memberDisputedAt
      });
    }

    if (events?.cancelledAt) {
      activity.push({
        title: "Invoice cancelled",
        description: toWSDateString(events.cancelledAt),
        date: events.cancelledAt
      });
    }

    if (events?.refundedAt) {
      activity.push({
        title: "Invoice refunded",
        description: toWSDateString(events.refundedAt),
        date: events.refundedAt
      });
    }

    return activity.sort((a, b) => {
      if (a.date > b.date) return -1;
      if (a.date < b.date) return 1;
      return 0;
    });
  }, [events, payerEmail, payeeEmail]);

  return <Activity activity={activity} />;
};
